import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Container, Paper, Title, Stack, Text, Code, Button, Alert } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

// Get admin users from Firestore
async function getAdminUsers(): Promise<string[]> {
  try {
    const { getDoc, doc } = await import('firebase/firestore');
    const { db } = await import('../../lib/firebase');
    const adminDoc = await getDoc(doc(db, 'settings', 'admin'));
    if (adminDoc.exists()) {
      return adminDoc.data()?.users || [];
    }
    return [];
  } catch (error) {
    console.error('Failed to load admin users:', error);
    return [];
  }
}

export default function AdminDebugPage() {
  const { user, isAdmin } = useAuth();
  const [adminUsers, setAdminUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAdminUsers = async () => {
      try {
        const users = await getAdminUsers();
        setAdminUsers(users);
      } catch (error) {
        console.error('Failed to load admin users:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadAdminUsers();
    } else {
      setLoading(false);
    }
  }, [user]);

  if (!user) {
    return (
      <Container size="md" my={40}>
        <Paper p="xl" withBorder>
          <Title order={2} mb="md">Debug Info</Title>
          <Alert icon={<IconInfoCircle size={16} />} color="blue">
            You are not logged in. Please log in first.
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="md" my={40}>
      <Paper p="xl" withBorder>
        <Title order={2} mb="md">Admin Debug Information</Title>
        
        <Stack gap="md">
          <div>
            <Text fw={500} mb="xs">Your User Information:</Text>
            <Code block>
              Email: {user.email || '(no email)'}
              {'\n'}UID: {user.uid}
            </Code>
          </div>

          <div>
            <Text fw={500} mb="xs">Admin Status:</Text>
            <Alert 
              icon={<IconInfoCircle size={16} />} 
              color={isAdmin ? 'green' : 'red'}
            >
              {isAdmin ? '✅ You ARE an admin' : '❌ You are NOT an admin'}
            </Alert>
          </div>

          <div>
            <Text fw={500} mb="xs">Current Admin Users List:</Text>
            {loading ? (
              <Text c="dimmed">Loading...</Text>
            ) : adminUsers.length === 0 ? (
              <Alert icon={<IconInfoCircle size={16} />} color="yellow">
                ⚠️ No admin users found. The settings/admin document might not exist.
              </Alert>
            ) : (
              <Code block>
                {JSON.stringify(adminUsers, null, 2)}
              </Code>
            )}
          </div>

          <Alert icon={<IconInfoCircle size={16} />} color="blue">
            <Text size="sm" fw={500} mb="xs">To fix authorization:</Text>
            <Text size="sm">
              1. Go to Firebase Console → Firestore Database
              <br />
              2. Navigate to: <Code>settings</Code> collection → <Code>admin</Code> document
              <br />
              3. Add a field named <Code>users</Code> (type: array)
              <br />
              4. Add your email <Code>{user.email}</Code> or UID <Code>{user.uid}</Code> to the array
              <br />
              5. Save the document
            </Text>
          </Alert>

          <Button onClick={() => window.location.reload()}>
            Refresh Status
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}

